/**
 * Shared box-dimensions pieces (2026-09-28, Rhys item 8).
 *
 * The edge-coloured box language (blue L, amber W, green H) started life
 * inside BoxDimensionsModal. It now lives here so the Products Bulk Update
 * bar can use the exact same illustration + compact `L × W × H cm` row
 * WITHOUT pulling in the modal (which ProductCard lazy-loads and Ship
 * imports) or the Ship chunk. Keep this file dependency-free apart from React.
 */

import React from 'react';

export const EDGE = {
  length: { color: '#3b82f6', label: 'L' },
  width: { color: '#f59e0b', label: 'W' },
  height: { color: '#22c55e', label: 'H' }
};

export const DIM_ORDER = ['length', 'width', 'height'];

/*
 * The inputs sit ON the box edges, and the box SCALES live to the entered
 * proportions — type 30×10×5 and you see a long flat box. Everything is
 * computed in one stage coordinate space (470×248) so the edges and their
 * inputs move together. Blank values fall back to a pleasant default shape;
 * each axis is floored so the box never collapses into an invisible sliver.
 */
export function boxGeometry(values) {
  const l = Math.max(0, parseFloat(values.length) || 0);
  const w = Math.max(0, parseFloat(values.width) || 0);
  const h = Math.max(0, parseFloat(values.height) || 0);
  const anyEntered = l > 0 || w > 0 || h > 0;
  const maxCm = Math.max(l, w, h, 1);
  const norm = (v, def) => (anyEntered && v > 0 ? Math.max(0.18, v / maxCm) : def);
  const nl = norm(l, 0.85);
  const nw = norm(w, 0.5);
  const nh = norm(h, 0.6);

  const LX = Math.round(160 * nl);  // front-edge run (px)
  const HY = Math.round(115 * nh);  // vertical run
  const DX = Math.round(62 * nw);   // receding run x
  const DY = Math.round(43 * nw);   // receding run y
  const x0 = 96;                    // front-bottom-left anchor
  const y0 = 182;
  return {
    x0, y0, LX, HY, DX, DY,
    front: `${x0},${y0 - HY} ${x0 + LX},${y0 - HY} ${x0 + LX},${y0} ${x0},${y0}`,
    top: `${x0},${y0 - HY} ${x0 + DX},${y0 - HY - DY} ${x0 + LX + DX},${y0 - HY - DY} ${x0 + LX},${y0 - HY}`,
    side: `${x0 + LX},${y0 - HY} ${x0 + LX + DX},${y0 - HY - DY} ${x0 + LX + DX},${y0 - DY} ${x0 + LX},${y0}`
  };
}

/** The svg is absolute — give it a sized, position:relative parent. */
export function BoxDiagram({ geo, width = 470, height = 248 }) {
  return (
    <svg viewBox="0 0 470 248" width={width} height={height} aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0 }}>
      <polygon points={geo.front} fill="rgba(148,163,184,0.18)" stroke="#94a3b8" strokeWidth="1" />
      <polygon points={geo.top} fill="rgba(148,163,184,0.30)" stroke="#94a3b8" strokeWidth="1" />
      <polygon points={geo.side} fill="rgba(148,163,184,0.24)" stroke="#94a3b8" strokeWidth="1" />
      {/* Length — front bottom edge */}
      <line x1={geo.x0} y1={geo.y0} x2={geo.x0 + geo.LX} y2={geo.y0} stroke={EDGE.length.color} strokeWidth="6" strokeLinecap="round" />
      {/* Width — receding bottom edge */}
      <line x1={geo.x0 + geo.LX} y1={geo.y0} x2={geo.x0 + geo.LX + geo.DX} y2={geo.y0 - geo.DY} stroke={EDGE.width.color} strokeWidth="6" strokeLinecap="round" />
      {/* Height — front left vertical edge */}
      <line x1={geo.x0} y1={geo.y0 - geo.HY} x2={geo.x0} y2={geo.y0} stroke={EDGE.height.color} strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Small inline box drawn to the given proportions with the same coloured edges
 * (Rhys 2026-10-09: "a little box that shows the dimensions"). Crops the 470×248
 * stage to the box's own extent, so it reads at icon size. Decorative.
 */
export function MiniBox({ length, width, height, w = 40, h = 30, className, style }) {
  const g = boxGeometry({ length, width, height });
  const pad = 12;
  const minX = g.x0 - pad;
  const minY = g.y0 - g.HY - g.DY - pad;
  const vbW = g.LX + g.DX + pad * 2;
  const vbH = g.HY + g.DY + pad * 2;
  const edge = (x1, y1, x2, y2, key) => (
    <line data-edge={key} x1={x1} y1={y1} x2={x2} y2={y2} stroke={EDGE[key].color} strokeWidth="9" strokeLinecap="round" />
  );
  return (
    <svg viewBox={`${minX} ${minY} ${vbW} ${vbH}`} width={w} height={h} aria-hidden="true" className={className} style={style} preserveAspectRatio="xMidYMid meet">
      <polygon points={g.front} fill="rgba(148,163,184,0.18)" stroke="#94a3b8" strokeWidth="3" />
      <polygon points={g.top} fill="rgba(148,163,184,0.30)" stroke="#94a3b8" strokeWidth="3" />
      <polygon points={g.side} fill="rgba(148,163,184,0.24)" stroke="#94a3b8" strokeWidth="3" />
      {edge(g.x0, g.y0, g.x0 + g.LX, g.y0, 'length')}
      {edge(g.x0 + g.LX, g.y0, g.x0 + g.LX + g.DX, g.y0 - g.DY, 'width')}
      {edge(g.x0, g.y0 - g.HY, g.x0, g.y0, 'height')}
    </svg>
  );
}

/**
 * Compact `L [__] × W [__] × H [__] cm` row with edge-coloured letters.
 * Controlled: `values` + `onChange(field, value)`. Enter walks L → W → H and
 * then calls `onEnterLast`; Escape calls `onEscape`. Colours are passed in so
 * the popover keeps its darkMode hexes and the bulk bar uses --app-* tokens.
 */
export function BoxDimensionsRow({
  values,
  onChange,
  onEnterLast,
  onEscape,
  ink,
  line,
  muted,
  autoFocus = false,
  disabled = false,
  style
}) {
  return (
    <div
      data-dim-group=""
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '8px', flexWrap: 'wrap', ...style }}
    >
      {DIM_ORDER.map((field, idx) => (
        <React.Fragment key={field}>
          {idx > 0 && <span style={{ color: muted }}>×</span>}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontWeight: 800, fontSize: '12px', color: EDGE[field].color }}>{EDGE[field].label}</span>
            <input
              type="number"
              className="pc-num"
              inputMode="decimal"
              min="0"
              step="0.1"
              aria-label={`${field[0].toUpperCase()}${field.slice(1)} (cm)`}
              autoFocus={autoFocus && field === 'length'}
              disabled={disabled}
              data-dim={field}
              value={values[field]}
              onChange={(e) => onChange(field, e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  // Enter walks L → W → H then saves. preventDefault stops an
                  // enclosing <form> from also doing an implicit submit.
                  e.preventDefault();
                  const next = DIM_ORDER[DIM_ORDER.indexOf(field) + 1];
                  if (!next) onEnterLast?.();
                  else e.target.closest('[data-dim-group]')?.querySelector(`input[data-dim="${next}"]`)?.focus();
                }
                if (e.key === 'Escape') onEscape?.();
              }}
              style={{
                width: '56px', textAlign: 'center', padding: '5px 4px',
                fontSize: '13px', borderRadius: '6px',
                border: `1px solid ${line}`, background: 'transparent',
                color: ink, outline: 'none'
              }}
            />
          </label>
        </React.Fragment>
      ))}
      <span style={{ fontSize: '11px', color: muted }}>cm</span>
    </div>
  );
}

/**
 * Inline "bar" variant for bulk edits (Products Bulk Update → Dimensions).
 * Small live diagram + the compact row + a green submit button with a custom
 * label. Partial input is allowed: the button enables once ANY field is
 * filled, and `onSubmit(values)` receives the raw strings — the caller
 * decides which filled fields to write. Themed with --app-* tokens.
 */
export function BoxDimensionsBar({
  values,
  onChange,
  onSubmit,
  submitLabel = 'Save dimensions',
  submittingLabel = 'Saving…',
  submitting = false,
  disabled = false,
  children
}) {
  const anyFilled = DIM_ORDER.some((f) => String(values[f] ?? '').trim() !== '');
  const canSubmit = anyFilled && !submitting && !disabled;
  const geo = boxGeometry(values);
  const ink = 'var(--app-ink)';
  const line = 'var(--app-border-strong, var(--app-border))';
  const muted = 'var(--app-ink-muted)';

  const submit = () => {
    if (canSubmit) onSubmit(values);
  };

  return (
    <form
      aria-label="Bulk box dimensions"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      style={{ color: ink }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', width: '128px', height: '68px', flexShrink: 0 }}>
          <BoxDiagram geo={geo} width={128} height={68} />
        </div>
        <BoxDimensionsRow
          values={values}
          onChange={onChange}
          onEnterLast={submit}
          ink={ink}
          line={line}
          muted={muted}
          disabled={disabled || submitting}
          style={{ marginTop: 0, justifyContent: 'flex-start', flex: 1 }}
        />
        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            padding: '7px 16px', fontSize: '13px', fontWeight: 700, borderRadius: '8px',
            border: 'none', background: '#22a06b', color: '#fff',
            cursor: canSubmit ? 'pointer' : 'default',
            opacity: canSubmit ? 1 : 0.5, flexShrink: 0
          }}
        >
          {submitting ? submittingLabel : submitLabel}
        </button>
      </div>
      {children}
    </form>
  );
}
